import React from 'react';
import AdminEmptyState from './AdminEmptyState';
import { copyTextToClipboard, normalizeAdminExportRows } from '../../admin/adminExportUtils';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * @param {{
 *   columns: Array<{ key: string, header: string }>,
 *   rows: Array<Record<string, unknown>>,
 *   emptyMessage?: string,
 *   emptyVariant?: 'default' | 'noResults',
 *   compact?: boolean,
 *   sortable?: boolean,
 *   sortKey?: string | null,
 *   sortDir?: 'asc' | 'desc' | null,
 *   onSortChange?: (columnKey: string) => void,
 *   copyRowKeys?: string[] | null,
 * }} props
 */
export default function AdminDataTable({
  columns,
  rows,
  emptyMessage,
  emptyVariant = 'default',
  compact = false,
  sortable = false,
  sortKey = null,
  sortDir = null,
  onSortChange,
  copyRowKeys = null,
}) {
  const { t } = useLanguage();
  if (!columns?.length) return null;

  const showCopyCol = Array.isArray(copyRowKeys) && copyRowKeys.length > 0;

  if (!rows?.length) {
    return (
      <AdminEmptyState
        message={emptyMessage || ''}
        variant={emptyVariant === 'noResults' ? 'noResults' : 'default'}
      />
    );
  }

  function cellValue(row, key) {
    const v = row[key];
    if (v == null || v === '') return '—';
    if (typeof v === 'boolean') return v ? '✓' : '—';
    if (React.isValidElement(v)) return v;
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  }

  function sortIndicator(key) {
    if (!sortable || sortKey !== key || !sortDir) return null;
    return <span className="admin-hub-kit-sort-ind">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>;
  }

  async function copyRow(row) {
    const keys = copyRowKeys || columns.map((c) => c.key).filter((k) => !String(k).startsWith('__'));
    const norm = normalizeAdminExportRows([row], keys)[0];
    await copyTextToClipboard(JSON.stringify(norm));
  }

  return (
    <div className="admin-hub-kit-table-wrap">
      <table className={`admin-hub-kit-table${compact ? ' admin-hub-kit-table--compact' : ''}`}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key}>
                {sortable && onSortChange ? (
                  <button type="button" className="admin-hub-kit-th-btn" onClick={() => onSortChange(c.key)}>
                    {c.header}
                    {sortIndicator(c.key)}
                  </button>
                ) : (
                  c.header
                )}
              </th>
            ))}
            {showCopyCol ? (
              <th className="admin-hub-kit-th--action" scope="col">
                {t('adminExport.copy')}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.__rowKey != null ? String(row.__rowKey) : i}>
              {columns.map((c) => (
                <td key={c.key}>{cellValue(row, c.key)}</td>
              ))}
              {showCopyCol ? (
                <td className="admin-hub-kit-td--action">
                  <button type="button" className="admin-hub-kit-icon-btn" onClick={() => copyRow(row)}>
                    {t('adminExport.copyRow')}
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
